import { useState, useEffect } from 'react';
import {
    Plus,
    SearchLg,
    Edit01,
    Trash01,
    UserPlus01,
    Mail01,
    CheckCircle,
    XCircle
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { teamService } from '@/data/mock-team';
import type { TeamMember, TeamRole, TeamMemberStatus } from '@/types/team';
import { useTranslation } from 'react-i18next';

export const ShipperTeamPage = () => {
    const { t } = useTranslation(['pages:team', 'common']);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<TeamRole | ''>('');
    const [statusFilter, setStatusFilter] = useState<TeamMemberStatus | ''>('');
    const [showModal, setShowModal] = useState(false);
    const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: 'Viewer' as TeamRole,
        status: 'Active' as TeamMemberStatus
    });

    const roleOptions = [
        { id: '', label: t('list.allRoles') },
        { id: 'Admin', label: 'Admin' },
        { id: 'Editor', label: 'Editor' },
        { id: 'Viewer', label: 'Viewer' }
    ];

    const statusOptions = [
        { id: '', label: t('list.allStatuses') },
        { id: 'Active', label: t('status.active', { ns: 'types' }) },
        { id: 'Inactive', label: t('status.inactive', { ns: 'types' }) }
    ];

    const roleFormOptions = [
        { id: 'Admin', label: 'Admin' },
        { id: 'Editor', label: 'Editor' },
        { id: 'Viewer', label: 'Viewer' }
    ];

    const statusFormOptions = [
        { id: 'Active', label: 'Attivo' },
        { id: 'Inactive', label: 'Inattivo' }
    ];

    useEffect(() => {
        const loadTeamMembers = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = localStorage.getItem('accountId') || '1';

            const members = teamService.getByAccountId(accountId);
            setTeamMembers(members);
            setIsLoading(false);
        };
        loadTeamMembers();
    }, []);

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'Admin': return 'error';
            case 'Editor': return 'warning';
            case 'Viewer': return 'brand';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Inactive': return 'error';
            default: return 'gray';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Active': return CheckCircle;
            case 'Inactive': return XCircle;
            default: return XCircle;
        }
    };

    const handleAddMember = () => {
        setEditingMember(null);
        setFormData({
            name: '',
            email: '',
            role: 'Viewer',
            status: 'Active'
        });
        setShowModal(true);
    };

    const handleEditMember = (member: TeamMember) => {
        setEditingMember(member);
        setFormData({
            name: member.name,
            email: member.email,
            role: member.role,
            status: member.status
        });
        setShowModal(true);
    };

    const handleSaveMember = () => {
        const accountId = localStorage.getItem('accountId') || '1';

        if (editingMember) {
            // Update existing member
            const success = teamService.update(editingMember.id, {
                id: editingMember.id,
                ...formData,
                accountId
            });
            if (success) {
                // Reload data
                window.location.reload();
            }
        } else {
            // Create new member
            const success = teamService.create({
                ...formData,
                accountId
            });
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const handleDeleteMember = (memberId: string) => {
        if (window.confirm(t('list.confirmDelete'))) {
            const success = teamService.delete(memberId);
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const filteredMembers = teamMembers.filter(member => {
        const matchesSearch = !searchTerm ||
            member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = !roleFilter || member.role === roleFilter;
        const matchesStatus = !statusFilter || member.status === statusFilter;

        return matchesSearch && matchesRole && matchesStatus;
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <Button
                    onClick={handleAddMember}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    iconLeading={Plus}
                >
                    {t('list.addMember')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('list.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.rolePlaceholder')}
                        items={roleOptions}
                        selectedKey={roleFilter}
                        onSelectionChange={(key) => setRoleFilter(key as TeamRole | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as TeamMemberStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Team Members Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('list.title')} (${filteredMembers.length})`}
                    description={t('list.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.headers.name')} isRowHeader />
                        <Table.Head label={t('list.headers.email')} />
                        <Table.Head label={t('list.headers.role')} />
                        <Table.Head label={t('list.headers.status')} />
                        <Table.Head label={t('list.headers.lastLogin')} />
                        <Table.Head label={t('list.headers.inviteDate')} />
                        <Table.Head label={t('list.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredMembers.map((member) => {
                            const StatusIcon = getStatusIcon(member.status);
                            return (
                                <Table.Row key={member.id}>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{member.name}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Mail01 className="size-4 text-tertiary" />
                                            <span className="text-sm text-tertiary">{member.email}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getRoleColor(member.role)} size="sm">
                                            {member.role}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <StatusIcon className="size-4" />
                                            <Badge color={getStatusColor(member.status)} size="sm">
                                                {member.status === 'Active' ? t('list.statusLabels.active') : t('list.statusLabels.inactive')}
                                            </Badge>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {member.lastLoginAt
                                                ? member.lastLoginAt.toLocaleDateString('it-IT')
                                                : t('list.never')
                                            }
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {member.invitedAt.toLocaleDateString('it-IT')}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEditMember(member)}
                                                className="text-brand-secondary hover:text-brand-secondary_hover"
                                            >
                                                <Edit01 className="size-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteMember(member.id)}
                                                className="text-error-600 hover:text-error-700"
                                            >
                                                <Trash01 className="size-4" />
                                            </button>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {filteredMembers.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <UserPlus01 className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.noMembers.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || roleFilter || statusFilter
                                ? t('list.noMembers.message')
                                : t('list.noMembers.emptyMessage')
                            }
                        </p>
                    </div>
                    {!searchTerm && !roleFilter && !statusFilter && (
                        <Button
                            onClick={handleAddMember}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                            iconLeading={Plus}
                        >
                            {t('list.noMembers.addFirst')}
                        </Button>
                    )}
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <ModalOverlay isOpen={showModal} onOpenChange={setShowModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {editingMember ? t('list.modal.editTitle') : t('list.modal.addTitle')}
                                    </h2>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.name')}
                                        </label>
                                        <Input
                                            placeholder={t('list.modal.fields.namePlaceholder')}
                                            value={formData.name}
                                            onChange={(value) => setFormData({ ...formData, name: value })}
                                            size="md"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.email')}
                                        </label>
                                        <Input
                                            placeholder={t('list.modal.fields.emailPlaceholder')}
                                            value={formData.email}
                                            onChange={(value) => setFormData({ ...formData, email: value })}
                                            size="md"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.role')}
                                        </label>
                                        <Select
                                            placeholder={t('list.modal.fields.role', { ns: 'common' })}
                                            items={roleFormOptions}
                                            selectedKey={formData.role}
                                            onSelectionChange={(key) => setFormData({ ...formData, role: key as TeamRole })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                        <p className="text-xs text-tertiary mt-1">
                                            {teamService.getRoleDescription(formData.role)}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.status')}
                                        </label>
                                        <Select
                                            placeholder={t('list.modal.fields.status', { ns: 'common' })}
                                            items={statusFormOptions}
                                            selectedKey={formData.status}
                                            onSelectionChange={(key) => setFormData({ ...formData, status: key as TeamMemberStatus })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    <div className="flex justify-end gap-3">
                                        <Button
                                            onClick={() => setShowModal(false)}
                                            color="secondary"
                                            size="md"
                                        >
                                            {t('list.modal.cancel')}
                                        </Button>
                                        <Button
                                            onClick={handleSaveMember}
                                            disabled={!formData.name || !formData.email}
                                            color="primary"
                                            size="md"
                                        >
                                            {editingMember ? t('list.modal.save') : t('list.modal.create')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};
